/**
 * Auth API hooks. Thin wrappers over TanStack Query that call the backend
 * and update the auth store.
 */

import { useMutation, useQuery } from "@tanstack/react-query";

import { api, AxiosError } from "@/lib/api";
import { getRefreshToken } from "@/lib/auth";
import { useAuthStore } from "@/stores/auth";
import type {
  AuthSession,
  LoginInput,
  MeResponse,
  MultipleMembershipsResponse,
  SignUpInput,
} from "@/types/api";

// ---------- Sign up ----------

export function useSignUp() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: async (input: SignUpInput): Promise<AuthSession> => {
      const r = await api.post<AuthSession>("/v1/auth/signup/", input);
      return r.data;
    },
    onSuccess: (data) => setSession(data),
  });
}

// ---------- Login ----------

export type LoginResult =
  | { kind: "session"; session: AuthSession }
  | { kind: "multiple"; memberships: MultipleMembershipsResponse["memberships"] };

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation<LoginResult, AxiosError, LoginInput>({
    mutationFn: async (input) => {
      const r = await api.post("/v1/auth/login/", input, {
        // Status 300 is Multiple Choices — let axios pass it through
        validateStatus: (s) => s === 200 || s === 300,
      });
      if (r.status === 200) {
        return { kind: "session", session: r.data as AuthSession };
      }
      return {
        kind: "multiple",
        memberships: (r.data as MultipleMembershipsResponse).memberships,
      };
    },
    onSuccess: (result) => {
      if (result.kind === "session") setSession(result.session);
    },
  });
}

// ---------- Logout ----------

export function useLogout() {
  const clearSession = useAuthStore((s) => s.clearSession);
  return useMutation({
    mutationFn: async () => {
      const refresh = getRefreshToken();
      if (refresh) {
        try {
          await api.post("/v1/auth/logout/", { refresh });
        } catch {
          // Logout is best-effort; clear local state regardless
        }
      }
    },
    onSettled: () => clearSession(),
  });
}

// ---------- Email verification ----------

export function useVerifyEmail() {
  return useMutation<void, AxiosError, { token: string }>({
    mutationFn: async ({ token }) => {
      await api.post("/v1/auth/verify-email/", { token });
    },
  });
}

export function useResendVerificationEmail() {
  return useMutation<void, AxiosError>({
    mutationFn: async () => {
      await api.post("/v1/auth/send-verification-email/");
    },
  });
}

// ---------- Onboarding ----------

export interface OnboardingPayload {
  company?: Record<string, unknown>;
  govt_ids?: Record<string, unknown>;
  address?: Record<string, unknown>;
  frequency?: Record<string, unknown>;
  branch?: Record<string, unknown>;
  employee?: Record<string, unknown>;
}

export function useFinishOnboarding() {
  return useMutation<{ branch_id?: string; employee_id?: string }, AxiosError, OnboardingPayload>({
    mutationFn: async (payload) => {
      const r = await api.post("/v1/auth/onboarding/finish/", payload);
      return r.data;
    },
  });
}

// ---------- Password reset ----------

export function useRequestPasswordReset() {
  return useMutation<void, AxiosError, { email: string }>({
    mutationFn: async ({ email }) => {
      await api.post("/v1/auth/request-password-reset/", { email });
    },
  });
}

export function useResetPassword() {
  return useMutation<void, AxiosError, { token: string; password: string }>({
    mutationFn: async ({ token, password }) => {
      await api.post("/v1/auth/reset-password/", { token, password });
    },
  });
}

// ---------- Me (current user + tenant + role) ----------

export function useMe() {
  const isAuth = useAuthStore((s) => s.user !== null);
  const setUser = useAuthStore((s) => s.setUser);
  const setTenant = useAuthStore((s) => s.setTenant);

  return useQuery<MeResponse>({
    queryKey: ["me"],
    queryFn: async () => {
      const r = await api.get<MeResponse>("/v1/auth/me/");
      // Backfill the store with details JWT didn't carry
      setUser(r.data.user);
      if (r.data.tenant) setTenant(r.data.tenant);
      return r.data;
    },
    enabled: isAuth,
    staleTime: 60_000,
    retry: 1,
  });
}
